import { supabase } from '../config/supabase';

export interface ComprehensiveUserData {
  // Core productivity data
  projects: Array<{
    id: string;
    name: string;
    description: string;
    color: string;
    createdAt: number;
  }>;
  tasks: Array<{
    id: string;
    title: string;
    description: string;
    status: string;
    priority: string;
    dueDate: string;
    estimatedPomodoros: number;
    completedPomodoros: number;
    projectId: string;
    createdAt: number;
  }>;
  pomodoros: Array<{
    id: string;
    startTime: number;
    endTime: number;
    duration: number;
    type: string;
    title: string;
    notes: string;
    completed: boolean;
    taskId: string;
    projectId: string;
  }>;

  // Goals and achievements
  goals: Array<{
    id: string;
    title: string;
    description: string;
    type: string;
    period: string;
    target: number;
    current: number;
    startDate: number;
    endDate: number;
  }>;
  achievements: Array<{
    id: string;
    name: string;
    description: string;
    icon: string;
    unlockedAt: number;
  }>;

  // Statistics and rankings
  userStats: {
    totalFocusTime: number;
    completedPomodoros: number;
    currentStreak: number;
    level: number;
    experience: number;
  };
  weeklyStats: {
    focusTime: number;
    completedPomodoros: number;
    week: string;
  };

  // Social data
  friends: Array<{
    id: string;
    name: string;
    email: string;
    avatarUrl: string;
    focusTime: number;
    level: number;
  }>;
  friendRequests: Array<{
    id: string;
    senderName: string;
    senderAvatar: string;
    createdAt: number;
  }>;

  // Settings and preferences
  settings: {
    timer: {
      pomodoro: number;
      shortBreak: number;
      longBreak: number;
      longBreakInterval: number;
    };
    notifications: {
      enabled: boolean;
      sound: boolean;
      desktop: boolean;
    };
    appearance: {
      theme: string;
      compactMode: boolean;
    };
  };

  // Community data
  communityPosts: Array<{
    id: string;
    content: string;
    likes: number;
    comments: number;
    createdAt: number;
  }>;
  sharedGoals: Array<{
    id: string;
    title: string;
    description: string;
    participants: number;
    progress: number;
  }>;

  // Current state
  currentTimerState: {
    phase: string;
    isActive: boolean;
    completedPomodoros: number;
  };
  currentTime: number;
}

class UserDataAggregator {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private isCacheValid(key: string): boolean {
    const cached = this.cache.get(key);
    if (!cached) return false;
    return Date.now() - cached.timestamp < this.CACHE_DURATION;
  }

  private setCache(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  private getCache(key: string): any {
    const cached = this.cache.get(key);
    return cached ? cached.data : null;
  }

  async getComprehensiveUserData(userId: string): Promise<ComprehensiveUserData> {
    const cacheKey = `user_data_${userId}`;
    
    if (this.isCacheValid(cacheKey)) {
      console.log('🔍 Using cached user data');
      return this.getCache(cacheKey);
    }

    console.log('🔍 Fetching fresh user data from database');
    
    try {
      // Fetch all data in parallel for better performance
      const [
        projectsData,
        tasksData,
        pomodorosData,
        goalsData,
        achievementsData,
        userStatsData,
        weeklyStatsData,
        friendsData,
        friendRequestsData,
        communityPostsData,
        sharedGoalsData
      ] = await Promise.all([
        this.fetchProjects(userId),
        this.fetchTasks(userId),
        this.fetchPomodoros(userId),
        this.fetchGoals(userId),
        this.fetchAchievements(userId),
        this.fetchUserStats(userId),
        this.fetchWeeklyStats(userId),
        this.fetchFriends(userId),
        this.fetchFriendRequests(userId),
        this.fetchCommunityPosts(userId),
        this.fetchSharedGoals(userId)
      ]);

      const comprehensiveData: ComprehensiveUserData = {
        projects: projectsData,
        tasks: tasksData,
        pomodoros: pomodorosData,
        goals: goalsData,
        achievements: achievementsData,
        userStats: userStatsData,
        weeklyStats: weeklyStatsData,
        friends: friendsData,
        friendRequests: friendRequestsData,
        settings: {
          timer: {
            pomodoro: 25,
            shortBreak: 5,
            longBreak: 15,
            longBreakInterval: 4
          },
          notifications: {
            enabled: true,
            sound: true,
            desktop: true
          },
          appearance: {
            theme: 'light',
            compactMode: false
          }
        },
        communityPosts: communityPostsData,
        sharedGoals: sharedGoalsData,
        currentTimerState: {
          phase: 'work',
          isActive: false,
          completedPomodoros: 0
        },
        currentTime: Date.now()
      };

      this.setCache(cacheKey, comprehensiveData);
      return comprehensiveData;
    } catch (error) {
      console.error('Error fetching comprehensive user data:', error);
      throw error;
    }
  }

  private async fetchProjects(userId: string) {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(project => ({
      id: project.id,
      name: project.name,
      description: project.description,
      color: project.color,
      createdAt: new Date(project.created_at).getTime()
    }));
  }

  private async fetchTasks(userId: string) {
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(task => ({
      id: task.id,
      title: task.title,
      description: task.description,
      status: task.status,
      priority: task.priority,
      dueDate: task.due_date,
      estimatedPomodoros: task.estimated_pomodoros,
      completedPomodoros: task.completed_pomodoros,
      projectId: task.project_id,
      createdAt: new Date(task.created_at).getTime()
    }));
  }

  private async fetchPomodoros(userId: string) {
    const { data, error } = await supabase
      .from('pomodoros')
      .select('*')
      .eq('user_id', userId)
      .order('start_time', { ascending: false })
      .limit(100); // Limit to recent 100 pomodoros for performance

    if (error) throw error;
    return (data || []).map(pomodoro => ({
      id: pomodoro.id,
      startTime: new Date(pomodoro.start_time).getTime(),
      endTime: new Date(pomodoro.end_time).getTime(),
      duration: Math.floor((new Date(pomodoro.end_time).getTime() - new Date(pomodoro.start_time).getTime()) / 1000),
      type: pomodoro.type || 'work',
      title: pomodoro.title || 'Pomodoro Session',
      notes: pomodoro.notes,
      completed: pomodoro.completed !== undefined ? pomodoro.completed : true,
      taskId: pomodoro.task_id,
      projectId: pomodoro.project_id
    }));
  }

  private async fetchGoals(userId: string) {
    const { data, error } = await supabase
      .from('goals')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(goal => ({
      id: goal.id,
      title: goal.title,
      description: goal.description,
      type: goal.category,
      period: goal.period,
      target: goal.target_value,
      current: goal.current_value,
      startDate: new Date(goal.start_date).getTime(),
      endDate: new Date(goal.end_date).getTime()
    }));
  }

  private async fetchAchievements(userId: string) {
    const { data, error } = await supabase
      .from('achievements')
      .select('*')
      .eq('user_id', userId)
      .order('unlocked_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(achievement => ({
      id: achievement.id,
      name: achievement.name,
      description: achievement.description,
      icon: achievement.icon,
      unlockedAt: new Date(achievement.unlocked_at).getTime()
    }));
  }

  private async fetchUserStats(userId: string) {
    const { data, error } = await supabase
      .from('user_stats')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
    
    return data ? {
      totalFocusTime: data.total_focus_time || 0,
      completedPomodoros: data.completed_pomodoros || 0,
      currentStreak: data.current_streak || 0,
      level: data.level || 1,
      experience: data.experience || 0
    } : {
      totalFocusTime: 0,
      completedPomodoros: 0,
      currentStreak: 0,
      level: 1,
      experience: 0
    };
  }

  private async fetchWeeklyStats(userId: string) {
    const { data, error } = await supabase
      .from('weekly_stats')
      .select('*')
      .eq('user_id', userId)
      .order('week', { ascending: false })
      .limit(1);

    if (error && error.code !== 'PGRST116') throw error;
    
    return data && data.length > 0 ? {
      focusTime: data[0].focus_time || 0,
      completedPomodoros: data[0].completed_pomodoros || 0,
      week: data[0].week
    } : {
      focusTime: 0,
      completedPomodoros: 0,
      week: new Date().toISOString().split('T')[0]
    };
  }

  private async fetchFriends(userId: string) {
    const { data, error } = await supabase
      .from('friends')
      .select(`
        id,
        user1_id,
        user2_id,
        status
      `)
      .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
      .eq('status', 'accepted');

    if (error) throw error;

    const friendUserIds = (data || []).map(friend => 
      friend.user1_id === userId ? friend.user2_id : friend.user1_id
    );

    if (friendUserIds.length === 0) return [];

    const { data: friendUsersData, error: friendUsersError } = await supabase
      .from('users')
      .select('id, name, email, avatar_url')
      .in('id', friendUserIds);

    if (friendUsersError) throw friendUsersError;

    return (friendUsersData || []).map(user => ({
      id: user.id,
      name: user.name,
      email: user.email,
      avatarUrl: user.avatar_url,
      focusTime: 0, // Could be enhanced to get from user_stats
      level: 1 // Could be enhanced to get from user_stats
    }));
  }

  private async fetchFriendRequests(userId: string) {
    const { data, error } = await supabase
      .from('friend_requests')
      .select(`
        id,
        sender_id,
        receiver_id,
        status,
        created_at
      `)
      .eq('receiver_id', userId)
      .eq('status', 'pending');

    if (error) throw error;

    const senderUserIds = (data || []).map(request => request.sender_id);

    if (senderUserIds.length === 0) return [];

    const { data: senderUsersData, error: senderUsersError } = await supabase
      .from('users')
      .select('id, name, email, avatar_url')
      .in('id', senderUserIds);

    if (senderUsersError) throw senderUsersError;

    return (data || []).map(request => {
      const senderUser = senderUsersData?.find(u => u.id === request.sender_id);
      return {
        id: request.id,
        senderName: senderUser?.name || 'Unknown User',
        senderAvatar: senderUser?.avatar_url || '',
        createdAt: new Date(request.created_at).getTime()
      };
    });
  }

  private async fetchCommunityPosts(userId: string) {
    const { data, error } = await supabase
      .from('community_posts')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(10);

    if (error && error.code !== 'PGRST116') throw error;
    
    return (data || []).map(post => ({
      id: post.id,
      content: post.content,
      likes: post.likes || 0,
      comments: post.comments || 0,
      createdAt: new Date(post.created_at).getTime()
    }));
  }

  private async fetchSharedGoals(userId: string) {
    const { data, error } = await supabase
      .from('shared_goals')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(10);

    if (error && error.code !== 'PGRST116') throw error;
    
    return (data || []).map(goal => ({
      id: goal.id,
      title: goal.title,
      description: goal.description,
      participants: goal.participants || 0,
      progress: goal.progress || 0
    }));
  }

  // Clear cache when data is updated
  clearCache(userId?: string): void {
    if (userId) {
      this.cache.delete(`user_data_${userId}`);
    } else {
      this.cache.clear();
    }
  }

  // Get cached data without fetching
  getCachedData(userId: string): ComprehensiveUserData | null {
    const cacheKey = `user_data_${userId}`;
    return this.isCacheValid(cacheKey) ? this.getCache(cacheKey) : null;
  }
}

export const userDataAggregator = new UserDataAggregator();
